/**
 * The floating toolbar over the page. §6.3.
 *
 * Canva's arrangement, because it is the one customers arrive knowing: the
 * controls for whatever is selected sit in one strip above the page, the
 * everyday ones right there - font, size, colour, bold - and anything with
 * more to it (effects, position, a colour with a spectrum) opens a panel on the
 * left beside the tools. The page keeps the rest of the width.
 *
 * The strip changes with the selection. What it offers for a text is not what
 * it offers for a picture, and a control that does nothing for the thing
 * selected is not shown rather than shown greyed.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { GraphicLayer, Layer, ShapeLayer, TextLayer } from '../model/types';
import { isGradient } from '../model/types';
import { toCss } from '../model/units';
import { useEditor, useSelectedLayers } from '../state/store';
import { familyHas } from '../text/fonts';
import { shapeDef } from '../shapes/library';
import {
  cannotConvert,
  convertToPath,
  groupSelected,
  ungroupSelected,
} from '../state/actions';
import { maskPair, maskWithSelection } from '../state/masks';
import { CASES, graphicInk, graphicWidthMm, nextAlign, setField, setGraphicWidth } from './edits';
import { effectNames } from './EffectsPanel';
import { presetById } from '../effects/presets';
import { LengthSlider, Slider } from './Slider';

export type Panel = 'effects' | 'position' | 'settings' | 'font' | 'colour' | 'mask';
export type ColourTarget = 'text' | 'fill' | 'outline' | 'page';

interface Props {
  panel: Panel | null;
  onPanel: ( panel: Panel, target?: ColourTarget ) => void;
}

export function ContextBar( { panel, onPanel }: Props ) {
  const selected = useSelectedLayers();
  const cropping = useEditor( ( s ) => s.cropping );
  const shaping = useEditor( ( s ) => s.shaping );
  const preview = useEditor( ( s ) => s.view.preview );

  if ( preview ) return null;

  // Nothing selected: the page itself is what the tools are for - its
  // background, and its settings. The strip stays put rather than vanishing,
  // so the customer always knows where the controls are.
  if ( selected.length === 0 ) return <PageTools panel={ panel } onPanel={ onPanel } />;

  // The two modes that take the canvas over have one thing to say: finish.
  if ( cropping ) {
    return (
      <Bar label="Cropping">
        <span className="tb-ctx__note">Drag the picture to choose what shows</span>
        <button type="button" className="tb-ctx__btn" onClick={ () => useEditor.getState().endCrop( false ) }>
          Leave it
        </button>
        <button type="button" className="tb-ctx__btn tb-ctx__btn--primary" onClick={ () => useEditor.getState().endCrop() }>
          Done
        </button>
      </Bar>
    );
  }

  if ( shaping ) {
    return (
      <Bar label="Editing points">
        <span className="tb-ctx__note">Drag points · Alt-click for corner or curve · double-click the outline to add</span>
        <button type="button" className="tb-ctx__btn tb-ctx__btn--primary" onClick={ () => useEditor.getState().endShaping() }>
          Done
        </button>
      </Bar>
    );
  }

  if ( selected.length > 1 ) {
    return (
      <Bar label={ `${ selected.length } objects` }>
        <span className="tb-ctx__note">{ selected.length } selected</span>
        <Sep />
        <button type="button" className="tb-ctx__btn" onClick={ () => groupSelected() }>
          Group
        </button>
        { maskPair( selected ) && (
          <button
            type="button"
            className="tb-ctx__btn"
            data-action="use-as-mask"
            title="Show the picture through the other object"
            onClick={ () => maskWithSelection() }
          >
            Use as mask
          </button>
        ) }
        <PanelButton panel="position" current={ panel } onPanel={ onPanel } />
      </Bar>
    );
  }

  const layer = selected[ 0 ] as Layer;

  return (
    <Bar label={ `${ layer.type } tools` }>
      { layer.type === 'text' && <TextTools layer={ layer } panel={ panel } onPanel={ onPanel } /> }
      { layer.type === 'shape' && <ShapeTools layer={ layer } panel={ panel } onPanel={ onPanel } /> }
      { layer.type === 'graphic' && <GraphicTools layer={ layer } panel={ panel } onPanel={ onPanel } /> }

      { layer.type === 'image' && (
        <button
          type="button"
          className="tb-ctx__btn"
          disabled={ ! layer.asset || layer.locked }
          onClick={ () => useEditor.getState().beginCrop( layer.id, layer.crop ) }
        >
          { layer.mask?.layer ? 'Move picture' : 'Crop' }
        </button>
      ) }

      { layer.type === 'image' && layer.mask?.frame && ! layer.asset && (
        <button type="button" className="tb-ctx__btn tb-ctx__btn--primary" onClick={ () => useEditor.getState().askPanel( 'uploads' ) }>
          Add picture
        </button>
      ) }

      { layer.type === 'image' && <PanelButton panel="mask" current={ panel } onPanel={ onPanel } /> }

      {/*
        What a code holds is the only thing about it worth changing, and it was
        reachable only through the ... menu at the end of this bar - which is
        where a customer looks for "delete", not for "what does this point at".
      */}
      { layer.type === 'qr' && (
        <button
          type="button"
          className={ `tb-ctx__btn${ panel === 'settings' ? ' is-on' : '' }` }
          data-panel="qr"
          aria-pressed={ panel === 'settings' }
          onClick={ () => onPanel( 'settings' ) }
        >
          QR code
        </button>
      ) }

      { layer.type === 'group' && (
        <button type="button" className="tb-ctx__btn" onClick={ () => ungroupSelected() }>
          Ungroup
        </button>
      ) }

      <Sep />
      <Transparency layer={ layer } />

      { ( layer.type === 'text' || layer.type === 'shape' ) && (
        <button
          type="button"
          className={ `tb-ctx__btn${ panel === 'effects' ? ' is-on' : '' }` }
          data-panel="effects"
          aria-pressed={ panel === 'effects' }
          onClick={ () => onPanel( 'effects' ) }
        >
          { layer.effect
            ? `Effects: ${ presetById( layer.effect.preset )?.name ?? effectNames[ layer.effect.kind ] }`
            : 'Effects' }
        </button>
      ) }

      <PanelButton panel="position" current={ panel } onPanel={ onPanel } />
      {/* Lock, duplicate and delete are on the small menu over the object
          itself (canvas/SelectionMenu.tsx), as in Canva - not here as well. */}
      <PanelButton panel="settings" current={ panel } onPanel={ onPanel } />
    </Bar>
  );
}

/* ---------------------------------------------------------------
 * Per kind
 * ------------------------------------------------------------ */

function PageTools( { panel, onPanel }: { panel: Panel | null; onPanel: Props[ 'onPanel' ] } ) {
  const background = useEditor( ( s ) => s.doc?.pages[ s.pageIndex ]?.background ?? null );
  const name = useEditor( ( s ) => s.doc?.pages[ s.pageIndex ]?.name ?? 'Page' );
  const colour = background?.type === 'colour' ? background.value : null;

  return (
    <Bar label="Page tools">
      <span className="tb-ctx__note">{ name }</span>
      <button
        type="button"
        className={ `tb-ctx__btn tb-ctx__swatch${ panel === 'colour' ? ' is-on' : '' }` }
        data-panel="background"
        title="Background colour"
        aria-label="Background colour"
        onClick={ () => onPanel( 'colour', 'page' ) }
      >
        <span className={ `tb-ctx__chip${ colour ? '' : ' tb-ctx__chip--paper' }` } style={ colour ? { background: toCss( colour ) } : undefined } />
      </button>
      <PanelButton panel="settings" current={ panel } onPanel={ onPanel } />
    </Bar>
  );
}

function TextTools( { layer, panel, onPanel }: { layer: TextLayer; panel: Panel | null; onPanel: Props[ 'onPanel' ] } ) {
  const stock = familyHas( layer.font.family );
  const size = layer.font.size;
  const setSize = ( next: number ) =>
    setField( layer, 'font', { ...layer.font, size: Math.max( 1, Math.min( 400, Math.round( next * 2 ) / 2 ) ) }, 'Size' );

  return (
    <>
      <button
        type="button"
        className={ `tb-ctx__font${ panel === 'font' ? ' is-on' : '' }` }
        data-panel="font"
        title="Font"
        onClick={ () => onPanel( 'font' ) }
      >
        <span style={ { fontFamily: `"${ layer.font.family }", system-ui` } }>{ layer.font.family }</span>
        <span aria-hidden="true">▾</span>
      </button>

      <div className="tb-ctx__size" role="group" aria-label="Size in points">
        <button type="button" className="tb-ctx__btn tb-ctx__btn--icon" aria-label="Smaller" onClick={ () => setSize( size - 1 ) }>
          −
        </button>
        <input
          className="tb-ctx__number tb-num"
          type="number"
          value={ size }
          min={ 1 }
          step={ 0.5 }
          aria-label="Size"
          onChange={ ( event ) => {
            const next = parseFloat( event.target.value );

            if ( ! Number.isNaN( next ) ) setSize( next );
          } }
        />
        <button type="button" className="tb-ctx__btn tb-ctx__btn--icon" aria-label="Larger" onClick={ () => setSize( size + 1 ) }>
          +
        </button>
      </div>

      <button
        type="button"
        className={ `tb-ctx__btn tb-ctx__btn--icon tb-ctx__colour${ panel === 'colour' ? ' is-on' : '' }` }
        data-panel="colour"
        title="Text colour"
        aria-label="Text colour"
        onClick={ () => onPanel( 'colour', 'text' ) }
      >
        <span className="tb-ctx__a">A</span>
        <span
          className="tb-ctx__bar"
          style={ {
            background: isGradient( layer.fill )
              ? `linear-gradient(90deg, ${ layer.fill.stops.map( ( s ) => toCss( s.colour ) ).join( ', ' ) })`
              : toCss( layer.fill ),
          } }
        />
      </button>

      <Toggle
        on={ layer.font.weight >= 600 }
        disabled={ ! stock.bold }
        title={ stock.bold ? 'Bold' : `${ layer.font.family } has no bold face` }
        onClick={ () => setField( layer, 'font', { ...layer.font, weight: layer.font.weight >= 600 ? 400 : 700 }, 'Bold' ) }
      >
        <strong>B</strong>
      </Toggle>
      <Toggle
        on={ layer.font.style === 'italic' }
        disabled={ ! stock.italic }
        title={ stock.italic ? 'Italic' : `${ layer.font.family } has no italic face` }
        onClick={ () =>
          setField( layer, 'font', { ...layer.font, style: layer.font.style === 'italic' ? 'normal' : 'italic' }, 'Italic' )
        }
      >
        <em>I</em>
      </Toggle>
      <Toggle on={ layer.underline } title="Underline" onClick={ () => setField( layer, 'underline', ! layer.underline ) }>
        <span style={ { textDecoration: 'underline' } }>U</span>
      </Toggle>
      <Toggle on={ layer.strike } title="Strike through" onClick={ () => setField( layer, 'strike', ! layer.strike ) }>
        <span style={ { textDecoration: 'line-through' } }>S</span>
      </Toggle>

      <Pop label="aA" title="Letter case">
        <div className="tb-ctx__menu">
          { CASES.map( ( c ) => (
            <button key={ c.label } type="button" className="tb-ctx__item" onClick={ () => setField( layer, 'text', c.change( layer.text ), 'Case' ) }>
              <span className="tb-ctx__item-mark">{ c.label }</span> { c.title }
            </button>
          ) ) }
        </div>
      </Pop>

      <button
        type="button"
        className="tb-ctx__btn tb-ctx__btn--icon"
        title={ `Align: ${ layer.align } - click for ${ nextAlign( layer.align ) }` }
        aria-label={ `Alignment, ${ layer.align }` }
        onClick={ () => setField( layer, 'align', nextAlign( layer.align ), 'Align' ) }
      >
        <AlignIcon align={ layer.align } />
      </button>

      <Pop label={ <SpacingIcon /> } title="Spacing">
        <div className="tb-ctx__pane">
          <Slider
            label="Letter spacing"
            value={ Math.round( layer.letterSpacing * 100 ) }
            min={ -100 }
            max={ 500 }
            step={ 5 }
            onChange={ ( v ) => setField( layer, 'letterSpacing', v / 100, 'Letter spacing' ) }
          />
          <Slider
            label="Line spacing"
            value={ Math.round( layer.lineHeight * 100 ) }
            min={ 50 }
            max={ 300 }
            step={ 5 }
            onChange={ ( v ) => setField( layer, 'lineHeight', v / 100, 'Line spacing' ) }
          />
        </div>
      </Pop>
    </>
  );
}

function ShapeTools( { layer, panel, onPanel }: { layer: ShapeLayer; panel: Panel | null; onPanel: Props[ 'onPanel' ] } ) {
  const def = shapeDef( layer.shape );
  const fillCss = isGradient( layer.fill )
    ? `linear-gradient(90deg, ${ layer.fill.stops.map( ( s ) => toCss( s.colour ) ).join( ', ' ) })`
    : toCss( layer.fill );
  const reason = layer.shape === 'free' ? null : cannotConvert( layer );

  return (
    <>
      { ! def.strokeOnly && (
        <button
          type="button"
          className={ `tb-ctx__btn tb-ctx__swatch${ panel === 'colour' ? ' is-on' : '' }` }
          data-panel="colour"
          title="Fill"
          aria-label="Fill colour"
          onClick={ () => onPanel( 'colour', 'fill' ) }
        >
          <span className="tb-ctx__chip" style={ { background: fillCss } } />
        </button>
      ) }

      <button
        type="button"
        className="tb-ctx__btn tb-ctx__swatch"
        data-panel="outline"
        title={ def.strokeOnly ? 'Line colour and width' : 'Outline' }
        aria-label="Outline"
        onClick={ () => onPanel( 'colour', 'outline' ) }
      >
        <span
          className={ `tb-ctx__chip tb-ctx__chip--ring${ layer.stroke ? '' : ' tb-ctx__chip--none' }` }
          style={ layer.stroke ? { borderColor: toCss( layer.stroke.colour ) } : undefined }
        />
      </button>

      { def.adjust === 'radius' && (
        <Pop label="Corners" title="Corner rounding">
          <div className="tb-ctx__pane">
            <LengthSlider
              label="Corner"
              mm={ layer.radius }
              maxMm={ Math.min( layer.w, layer.h ) / 2 }
              onChange={ ( v ) => setField( layer, 'radius', v, 'Corner' ) }
            />
          </div>
        </Pop>
      ) }

      { def.adjust === 'points' && (
        <Pop label={ layer.shape === 'polygon' ? 'Sides' : 'Points' } title="How many">
          <div className="tb-ctx__pane">
            <Slider
              label={ layer.shape === 'polygon' ? 'Sides' : 'Points' }
              value={ layer.points }
              min={ 3 }
              max={ 30 }
              onChange={ ( v ) => setField( layer, 'points', v, 'Points' ) }
            />
          </div>
        </Pop>
      ) }

      <button
        type="button"
        className="tb-ctx__btn"
        disabled={ !! reason || layer.locked }
        title={ reason ?? 'Move the points of this shape' }
        onClick={ () => {
          if ( layer.shape === 'free' || convertToPath( layer.id ) ) useEditor.getState().beginShaping( layer.id );
        } }
      >
        Edit points
      </button>
    </>
  );
}

/**
 * An icon or a drawing from the library.
 *
 * Two things are worth changing about one and they are the two a customer asks
 * for first: what colour it is, and - since the library is outline icons - how
 * heavy its lines are. Both work on the whole drawing, because an icon is one
 * picture to the person looking at it however many paths it happens to be made
 * of.
 */
function GraphicTools( { layer, panel, onPanel }: { layer: GraphicLayer; panel: Panel | null; onPanel: Props[ 'onPanel' ] } ) {
  const ink = graphicInk( layer );
  const width = graphicWidthMm( layer );

  return (
    <>
      <button
        type="button"
        className={ `tb-ctx__btn tb-ctx__swatch${ panel === 'colour' ? ' is-on' : '' }` }
        data-panel="colour"
        title="Colour"
        aria-label="Colour"
        onClick={ () => onPanel( 'colour', 'fill' ) }
      >
        <span
          className={ `tb-ctx__chip${ ink ? '' : ' tb-ctx__chip--mixed' }` }
          style={ ink ? { background: toCss( ink ) } : undefined }
        />
      </button>

      { null !== width && (
        <Pop label="Thickness" title="How heavy the lines are">
          <div className="tb-ctx__pane">
            <LengthSlider
              label="Thickness"
              mm={ width }
              minMm={ 0.05 }
              // Never below what the drawing arrived at: an icon whose lines
              // are already heavier than the top of the slider would be thinned
              // by the first touch of a control the customer only opened to
              // look at.
              maxMm={ Math.max( 5, width * 2 ) }
              onChange={ ( v ) => setGraphicWidth( layer, v ) }
            />
          </div>
        </Pop>
      ) }
    </>
  );
}

/* ---------------------------------------------------------------
 * For every object
 * ------------------------------------------------------------ */

function Transparency( { layer }: { layer: Layer } ) {
  return (
    <Pop label={ <CheckerIcon /> } title="Transparency">
      <div className="tb-ctx__pane">
        <Slider
          label="Transparency"
          value={ Math.round( ( 1 - layer.opacity ) * 100 ) }
          onChange={ ( v ) => setField( layer, 'opacity', Math.round( 100 - v ) / 100, 'Transparency' ) }
        />
      </div>
    </Pop>
  );
}

const PANEL_LABEL: Record<Panel, string> = {
  effects: 'Effects',
  position: 'Position',
  settings: 'More',
  font: 'Font',
  colour: 'Colour',
  mask: 'Mask',
};

function PanelButton( { panel, current, onPanel }: { panel: Panel; current: Panel | null; onPanel: Props[ 'onPanel' ] } ) {
  const on = current === panel;

  return (
    <button
      type="button"
      className={ `tb-ctx__btn${ on ? ' is-on' : '' }` }
      data-panel={ panel }
      aria-pressed={ on }
      title={ panel === 'settings' ? 'Every setting for this object' : PANEL_LABEL[ panel ] }
      onClick={ () => onPanel( panel ) }
    >
      { panel === 'settings' ? '⋯' : PANEL_LABEL[ panel ] }
    </button>
  );
}

/* ---------------------------------------------------------------
 * Pieces
 * ------------------------------------------------------------ */

function Bar( { label, children }: { label: string; children: ReactNode } ) {
  return (
    <div className="tb-ctx" role="toolbar" aria-label={ label }>
      { children }
    </div>
  );
}

function Sep() {
  return <span className="tb-ctx__sep" role="separator" />;
}

function Toggle( {
  on,
  disabled,
  title,
  onClick,
  children,
}: {
  on: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: ReactNode;
} ) {
  return (
    <button
      type="button"
      className={ `tb-ctx__btn tb-ctx__btn--icon${ on ? ' is-on' : '' }` }
      aria-pressed={ on }
      disabled={ disabled }
      title={ title }
      aria-label={ title }
      onClick={ onClick }
    >
      { children }
    </button>
  );
}

/** A button with a small pane under it, closed by clicking away or Escape. */
function Pop( { label, title, children }: { label: ReactNode; title: string; children: ReactNode } ) {
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };
    // Caught before the editor's shortcuts and marked handled, so Escape
    // closes this and does not also deselect what it belongs to.
    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  return (
    <div className="tb-ctx__pop" ref={ wrap }>
      <button
        type="button"
        className={ `tb-ctx__btn${ typeof label === 'string' ? '' : ' tb-ctx__btn--icon' }${ open ? ' is-on' : '' }` }
        aria-expanded={ open }
        title={ title }
        aria-label={ title }
        onClick={ () => setOpen( ! open ) }
      >
        { label }
      </button>
      { open && (
        <div className="tb-ctx__drop" role="dialog" aria-label={ title }>
          { children }
        </div>
      ) }
    </div>
  );
}

/* ---------------------------------------------------------------
 * Icons, drawn rather than named
 * ------------------------------------------------------------ */

const Svg = ( { children }: { children: ReactNode } ) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    { children }
  </svg>
);

function AlignIcon( { align }: { align: TextLayer[ 'align' ] } ) {
  const rows = [ 16, 11, 14, 9 ];

  return (
    <Svg>
      { rows.map( ( w, i ) => {
        const width = align === 'justify' ? 16 : w;
        const x = align === 'left' || align === 'justify' ? 2 : align === 'right' ? 18 - width : 10 - width / 2;

        return <line key={ i } x1={ x } x2={ x + width } y1={ 4 + i * 4 } y2={ 4 + i * 4 } />;
      } ) }
    </Svg>
  );
}

const SpacingIcon = () => (
  <Svg>
    <path d="M10 2v16M6 5l4-3 4 3M6 15l4 3 4-3" />
  </Svg>
);

const CheckerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    { [ 0, 1, 2, 3, 4, 5 ].flatMap( ( r ) =>
      [ 0, 1, 2, 3, 4, 5 ].map( ( c ) =>
        ( r + c ) % 2 === 0 ? <rect key={ `${ r }-${ c }` } x={ c * 3 } y={ r * 3 } width="3" height="3" fill="currentColor" opacity={ 0.25 + ( c / 6 ) * 0.75 } /> : null
      )
    ) }
  </svg>
);

